import { packageText as pm, usePackageManagementLanguage } from "../../i18n/packageManagementPresentation";
/**
 * PlexChannelSelectorModal
 *
 * Opens when autodetect cannot determine the Plex channel (detectedChannel: "unknown").
 * Presents two options: Free or PlexPass.
 *
 * - Free selected  → caller resubmits with plexChannel: "free" (no creds needed)
 * - PlexPass selected → caller opens PlexCredentialModal with plexChannel: "plexpass"
 *
 * Security contract:
 *   - No credentials are collected here — this is a channel selector only
 *   - The selected channel is surfaced via onSelectFree / onSelectPlexPass callbacks
 */
import { type KeyboardEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { cn } from "../../utils/cn";

// ── Focus trap ────────────────────────────────────────────────────────────────

const FOCUSABLE_SELECTOR =
  "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

const trapFocus = (container: HTMLElement, event: KeyboardEvent<HTMLDivElement>) => {
  const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last?.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first?.focus();
  }
};

// ── Types ─────────────────────────────────────────────────────────────────────

type PlexChannelSelectorModalProps = {
  open: boolean;
  /** Called when the user selects Free and confirms. Caller resubmits with plexChannel: "free". */
  onSelectFree: () => void;
  /**
   * Called when the user selects PlexPass and confirms.
   * Caller skips the resubmit roundtrip and opens PlexCredentialModal directly
   * with plexChannel: "plexpass" ready to send on credential submit.
   */
  onSelectPlexPass: () => void;
  /** Called when the user cancels or dismisses without selecting. */
  onClose: () => void;
};

// ── Component ─────────────────────────────────────────────────────────────────

export const PlexChannelSelectorModal = ({
  open,
  onSelectFree,
  onSelectPlexPass,
  onClose,
}: PlexChannelSelectorModalProps) => {
  usePackageManagementLanguage();
  const titleId = useId();
  const [selected, setSelected] = useState<"free" | "plexpass">("plexpass");

  const dialogRef = useRef<HTMLDivElement>(null);
  const firstRadioRef = useRef<HTMLInputElement>(null);

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelected("plexpass");
    }
  }, [open]);

  // Focus first radio on open
  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      firstRadioRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(timer);
  }, [open]);

  // Dismiss on Escape
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  // Tab focus trap
  const handleDialogKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Tab" && dialogRef.current) {
        trapFocus(dialogRef.current, e);
      }
    },
    [],
  );

  const handleConfirm = useCallback(() => {
    if (selected === "free") {
      onSelectFree();
    } else {
      onSelectPlexPass();
    }
  }, [selected, onSelectFree, onSelectPlexPass]);

  if (!open) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={handleDialogKeyDown}
        className={cn(
          "w-full max-w-sm rounded-xl border border-border/50 bg-card shadow-xl",
          "animate-in fade-in-0 zoom-in-95 duration-200",
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border/40 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
              <Icon icon="simple-icons:plex" className="h-4 w-4 text-amber-500" aria-hidden="true" />
            </div>
            <div>
              <h2 id={titleId} className="text-base font-semibold text-foreground">
                {pm("selectPlexChannel")}</h2>
              <p className="text-xs text-muted-foreground">
                {pm("plexSubscription")}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={pm("closeDialog")}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md hover:bg-white/[0.06] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
          >
            <Icon icon="lucide:x" className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3" role="radiogroup" aria-label={pm("plexChannel")}>
          {/* Info notice */}
          <div className="flex items-start gap-2 rounded-lg bg-muted/30 border border-border/30 px-3 py-2.5">
            <Icon icon="lucide:info" className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              {pm("plexDetectFailed")}</p>
          </div>

          {/* PlexPass option */}
          <label
            className={cn(
              "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
              selected === "plexpass"
                ? "border-amber-500/50 bg-amber-500/5"
                : "border-border/40 bg-white/[0.02] hover:bg-white/[0.04]",
            )}
          >
            <input
              ref={firstRadioRef}
              type="radio"
              name="plex-channel"
              value="plexpass"
              checked={selected === "plexpass"}
              onChange={() => setSelected("plexpass")}
              className="mt-0.5 accent-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">PlexPass</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {pm("plexPassDescription")}</p>
            </div>
          </label>

          {/* Free option */}
          <label
            className={cn(
              "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
              selected === "free"
                ? "border-blue-500/50 bg-blue-500/5"
                : "border-border/40 bg-white/[0.02] hover:bg-white/[0.04]",
            )}
          >
            <input
              type="radio"
              name="plex-channel"
              value="free"
              checked={selected === "free"}
              onChange={() => setSelected("free")}
              className="mt-0.5 accent-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{pm("free")}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {pm("plexFreeDescription")}</p>
            </div>
          </label>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border/40 px-5 py-4">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
          >
            {pm("cancel")}</Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            className="gap-1.5"
          >
            {selected === "plexpass" ? (
              <>
                <Icon icon="lucide:lock" className="h-3.5 w-3.5" aria-hidden="true" />
                {pm("continue")}</>
            ) : (
              <>
                <Icon icon="lucide:arrow-right" className="h-3.5 w-3.5" aria-hidden="true" />
                {pm("continue")}</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};
